import React, { useState, useEffect } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { IconX } from '@tabler/icons-react';
import { updateMeasureStatus, fetchDefaultQuestions } from '../features/checkpointsSlice';
import InfoSquareIcon from '../assets/gptw_icons/Icons_rot/info-square.png';
import './QuestionStatusRow.css';

const QuestionStatusRow = ({
    checkpointIndex,
    questionIndex,
    question,
    checkpointId,
    unitId,
    readOnly = false,
}) => {
    const dispatch = useDispatch();
    
    // Get default questions from Redux store
    const defaultQuestions = useSelector(state => state.checkpoints.defaultQuestions);
    const questionsStatus = useSelector(state => state.checkpoints.questionsStatus);

    // Available answers for questions
    const availableAnswers = ["ja", "nein"];

    // Local state for immediate UI feedback
    const [localStatus, setLocalStatus] = useState(question.status || '');
    const [communicationDate, setCommunicationDate] = useState(question.communication_date || '');
    const [isInitialMount, setIsInitialMount] = useState(true);

    // Fetch default questions if not already loaded
    useEffect(() => {
        if (questionsStatus === 'idle') {
            dispatch(fetchDefaultQuestions());
        }
    }, [dispatch, questionsStatus]);

    // Initialize local state when question data changes
    useEffect(() => {
        // Only update local state when question data changes, not on every render
        setLocalStatus(question.status || '');
        setCommunicationDate(question.communication_date || '');
    }, [question.status, question.communication_date, unitId]);    

    // Mark initial mount as complete after first render
    useEffect(() => {
        setIsInitialMount(false);
    }, []);

    const handleStatusChange = (newStatus) => {
        // Skip if readonly
        if (readOnly) {
            return;
        }
        
        // Toggle behavior: if clicking on already-selected answer, unselect it
        const updatedStatus = newStatus === localStatus ? '' : newStatus;
        
        // Update local state immediately for UI responsiveness
        setLocalStatus(updatedStatus);
        
        // Only dispatch API call if we have a valid status ID and not on initial mount
        if (question.status_id && !isInitialMount) {
            console.log("Updating question status with ID:", question.id, "for unit:", unitId);
            
            dispatch(
                updateMeasureStatus({
                    unitId,
                    checkpointId,
                    statusId: question.status_id,
                    newStatus: updatedStatus,
                    newStatusDescription: question.status_description || '',
                    // Only send communication_date if it has a value, otherwise send null
                    communicationDate: communicationDate || null,
                    questionId: question.id
                })
            );
        }
    };
    
    const handleDateChange = (e) => {
        // Skip if readonly
        if (readOnly) {
            return;
        }
        
        const newDate = e.target.value;
        
        // Update local state
        setCommunicationDate(newDate);
        
        if (question.status_id && !isInitialMount) {
            dispatch(
                updateMeasureStatus({
                    unitId,
                    checkpointId,
                    statusId: question.status_id,
                    // Only include status if it has a value
                    newStatus: localStatus || null,
                    newStatusDescription: question.status_description || '',
                    // Send the new date or null if empty
                    communicationDate: newDate || null,
                    questionId: question.id
                })
            );
        }
    };

    const defaultQuestion = defaultQuestions.find((q) => q.id === question.question_id || q.id === question.id);
    const questionText =
        question.question_text ||
        defaultQuestion?.question_text ||
        question.question_tag ||
        defaultQuestion?.question_tag ||
        '';

    return (
        <div className="question-status-row">
            {/* Question text */}
            <div className="question-content">
                <p className="question-text-content">{questionText}</p>
                {question.hint && (
                    <small className="question-hint">
                        <i className="bi bi-info-circle me-1"></i>
                        {question.hint}
                    </small>
                )}
                {question.importance === 'high' && (
                    <span className="question-importance-badge">Wichtig</span>
                )}
            </div>

            {/* Answer controls row */}
            <div className="question-controls">
                {/* Ja/Nein checkbox options */}
                <div className="question-answer-options">
                    {availableAnswers.map((answer) => {
                        const isSelected = localStatus === answer;
                        return (
                            <div
                                key={answer}
                                className={`question-answer-option ${isSelected ? 'is-selected' : ''} ${readOnly ? 'is-disabled' : ''}`}
                                onClick={() => !readOnly && handleStatusChange(answer)}
                                role="radio"
                                aria-checked={isSelected}
                                aria-disabled={readOnly}
                                tabIndex={readOnly ? -1 : 0}
                                onKeyDown={(e) => {
                                    if (!readOnly && (e.key === 'Enter' || e.key === ' ')) {
                                        e.preventDefault();
                                        handleStatusChange(answer);
                                    }
                                }}
                            >
                                <div className="question-answer-checkbox">
                                    <IconX size={24} stroke={2.5} />
                                </div>
                                <span className="question-answer-label">{answer}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Date input */}
                <div className="question-date-input" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Form.Control
                        type="date"
                        value={communicationDate || ''}
                        onChange={handleDateChange}
                        disabled={readOnly}
                        className="question-date-field"
                    />
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="date-tooltip">
                                Datum der Umsetzung
                            </Tooltip>
                        }
                    >
                        <img
                            src={InfoSquareIcon}
                            alt="Info"
                            style={{ cursor: 'pointer', width: '20px', height: '20px', flexShrink: 0 }}
                        />
                    </OverlayTrigger>
                </div>
            </div>
        </div>
    );
};

export default QuestionStatusRow;